/**
 * Production adapter for the marker tools: the SAME events the structure
 * list publishes —
 *  - "+ New → Point" (`useStructureSet().createRoi(MARKER)`):
 *    `RoiCreationRequestedEvent(setUid, 'user', MARKER, position mm)`; the
 *    list uses the crosshair, the agent may pass an explicit position;
 *  - the name field: `RoiNameChangeRequestedEvent`;
 *  - the marker position fields: `RoiPointCoordinatesChangeRequestedEvent`
 *    (mm).
 * Like the list's command path, a rename / move arms the unsaved-changes
 * guard (creation's handler does so itself). The image check uses the
 * image grid (`Grid.includesPoint`). Browser glue covered by the manual
 * demo; the logic is unit-tested through fakes.
 */
import { vec3 } from 'gl-matrix';
import { useCallback } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import {
    domainEventBus,
    RoiCreationRequestedEvent,
    RoiNameChangeRequestedEvent,
    RoiPointCoordinatesChangeRequestedEvent,
} from '~/domain/events';
import { environmentState } from '~/state/atoms/engineAtoms';
import { hasUnsavedChangesState } from '~/state/atoms/patientAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { projectStructureFacts } from '../core/engineStructureFacts';
import { useLiveStructureSet } from '../structureManagement';
import type { MarkerDeps } from './toolRunner';

const xyz = (p: readonly number[]) => ({ x: p[0], y: p[1], z: p[2] });

export function useMarkerDeps(): () => Omit<
    MarkerDeps,
    'pendingMutationExists'
> {
    const env = useRecoilValue(environmentState);
    const structureSet = useRecoilValue(currentStructureSetState);
    const setHasUnsavedChanges = useSetRecoilState(hasUnsavedChangesState);
    const liveSet = useLiveStructureSet();

    return useCallback(
        () => ({
            structures: projectStructureFacts(structureSet, env),
            liveSet,
            currentContextId: () => env?.structureSet?.sopInstanceUID,
            crosshairMm: () => {
                const c = env?.image
                    ? env.planeIntersectionPoint.coordinates
                    : undefined;
                return c ? ([c[0], c[1], c[2]] as const) : undefined;
            },
            insideImage: (p) =>
                env?.image?.grid.includesPoint(
                    vec3.fromValues(p[0], p[1], p[2])
                ) ?? false,
            createMarker: async (p) => {
                const uid = structureSet?.dicomUid;
                if (!uid) {
                    throw new Error('no structure set is loaded');
                }
                await domainEventBus.publish(
                    new RoiCreationRequestedEvent(
                        uid,
                        'user',
                        RoiInterpretedType.MARKER,
                        xyz(p)
                    )
                );
            },
            rename: async (n, name) => {
                await domainEventBus.publish(
                    new RoiNameChangeRequestedEvent(n, name, 'user')
                );
                setHasUnsavedChanges(true);
            },
            setPosition: async (n, p) => {
                await domainEventBus.publish(
                    new RoiPointCoordinatesChangeRequestedEvent(
                        n,
                        xyz(p),
                        'user'
                    )
                );
                setHasUnsavedChanges(true);
            },
        }),
        [env, structureSet, setHasUnsavedChanges, liveSet]
    );
}
